import { safeLevelFromAvg } from '@/data/mockSafeData';

/**
 * Comportamientos elegibles para la frase de "siguiente nivel": solo los que están
 * por debajo del umbral bajo. Antes se pasaba a `buildNarrative` la lista completa de
 * tarjetas de crecimiento (los 4 más bajos, sin filtrar), así que una persona con todos
 * sus comportamientos entre 3.6 y 3.9 igual leía "enfócate en tu cuarto mejor
 * comportamiento" — la narrativa no debería sugerir nada cuando nada está realmente bajo.
 */
export function selectNarrativeGrowth<T extends { avg: number }>(
  growthCandidates: readonly T[],
  lowThreshold: number,
): T[] {
  return growthCandidates.filter(candidate => candidate.avg < lowThreshold);
}

export function buildNarrative(
  strengths: { name: string; avg: number }[],
  growth: { name: string; avg: number }[],
  currentAvg: number,
  pastAvg: number,
  tFn: (key: string, opts?: Record<string, unknown>) => string,
  levelLabels: readonly string[],
): string {
  const pieces: string[] = [];
  if (strengths.length > 0) {
    const { idx } = safeLevelFromAvg(strengths[0].avg);
    pieces.push(tFn('safe.narrativeStrength', { fund: strengths[0].name.toLowerCase(), level: levelLabels[idx] }));
    if (strengths.length > 1) {
      const others = strengths.slice(1).map(strength => strength.name.toLowerCase()).join(' y ');
      pieces.push(tFn('safe.narrativeConsistency', { others }));
    }
  }
  if (currentAvg > 0 && pastAvg > 0) {
    const diff = Math.round((currentAvg - pastAvg) * 10) / 10;
    if (diff > 0) pieces.push(tFn('safe.narrativeProgress'));
    else if (diff === 0) pieces.push(tFn('safe.narrativeStable'));
    else pieces.push(tFn('safe.narrativeVariation'));
  }
  if (growth.length > 0) {
    const { idx } = safeLevelFromAvg(growth[0].avg);
    const nextLabel = idx < 3 ? levelLabels[idx + 1] : levelLabels[3];
    pieces.push(tFn('safe.narrativeNextLevel', { fund: growth[0].name.toLowerCase(), level: nextLabel }));
  }
  return pieces.join(' ');
}
